<script lang="ts" setup>
import { ref, computed, watch, defineProps } from 'vue';
import { ChallengeProgressEntry } from '@/types/v2/task/challenge/configuration.types';
import { useChallengeForm } from '@/composables/tasks/challenge/useChallengeForm';
import { useTaskHistory } from '@/composables/tasks/useTaskHistory';

import ModalBox from "@/components/v2/Modal/ModalBox.vue";
import { useChallenge } from "@/composables/tasks/challenge/useChallenge";
import { useRecord } from "@/composables/tasks/challenge/useChallengeRecords";
import { useChallengeConfigurations } from "@/composables/tasks/challenge/useChallengeConfigurations";

const props = defineProps<{
    isOpen: boolean,
    entry: ChallengeProgressEntry | null,
    configKey: string,
    entryIndex: number,
    onClose?: () => void;
}>();

// ===================================================================
// Composables
// ===================================================================
const {updateChallengeRecord, addRevisionToRecord} = useRecord();
const {getChallengeRecordHistory} = useChallengeConfigurations();
const {configurations} = useChallenge();

const {
    challenged,
    weight,
    challengeDuration,
    comment: reason,
    isWeightDisabled,
    isChallengedDisabled,
    incrementChallenged,
    resetForm
} = useChallengeForm();

const {recordChange} = useTaskHistory();

// ===================================================================
// State
// ===================================================================
const originalEntry = ref<ChallengeProgressEntry | null>(null);
const configIndex = ref(-1);
const errors = ref({
    reason: ''
});

// ===================================================================
// Computed
// ===================================================================
const hasChanges = computed(() => {
    if (!originalEntry.value) return false;

    const challengedChanged = challenged.value !== originalEntry.value.challenged;
    const weightChanged = (weight.value || 0) !== (originalEntry.value.weight || 0);
    const durationChanged = (challengeDuration.value || 0) !== (originalEntry.value.challengeDuration || 0);

    return challengedChanged || weightChanged || durationChanged;
});

// Update isValid to be more permissive for valid changes
const isValid = computed(() => {
    // Allow revising down to 0 (e.g. death of fish correction)
    const hasValidValues =
        typeof challenged.value === 'number' &&
        Number.isFinite(challenged.value) &&
        challenged.value >= 0 &&
        typeof weight.value === 'number' &&
        Number.isFinite(weight.value) &&
        weight.value >= 0;

    // If there are changes, reason is required
    const hasValidReason = !hasChanges.value || (hasChanges.value && reason.value.trim() !== '');

    return hasValidValues && hasValidReason && !errors.value.reason;
});

// Format and sort history entries
const formattedHistory = computed(() => {
    if (!props.configKey || props.entryIndex === -1) return [];

    const entries = getChallengeRecordHistory(props.configKey, props.entryIndex);

    // Process and format entries for display
    const formatted = entries.map(entry => {
        let result = {
            field: entry.field,
            reason: entry.reason,
            timestamp: entry.timestamp,
            oldChallenged: undefined,
            newChallenged: undefined,
            oldWeight: undefined,
            newWeight: undefined,
            oldDuration: undefined,
            newDuration: undefined
        };

        // Extract values based on the field type
        if (entry.field === 'Challenge Administration' && typeof entry.oldValue === 'object' && typeof entry.newValue === 'object') {
            result.oldChallenged = entry.oldValue.challenged;
            result.newChallenged = entry.newValue.challenged;
            result.oldWeight = entry.oldValue.weight;
            result.newWeight = entry.newValue.weight;
            result.oldDuration = entry.oldValue.challengeDuration;
            result.newDuration = entry.newValue.challengeDuration;
        } else if (entry.field === 'Challenged Fish') {
            result.oldChallenged = entry.oldValue;
            result.newChallenged = entry.newValue;
        } else if (entry.field === 'Weight') {
            result.oldWeight = entry.oldValue;
            result.newWeight = entry.newValue;
        } else if (entry.field === 'Challenge Duration') {
            result.oldDuration = entry.oldValue;
            result.newDuration = entry.newValue;
        }

        return result;
    });

    // Sort by timestamp, newest first
    return formatted.sort((a, b) => {
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
});

// ===================================================================
// Methods
// ===================================================================
const setEntry = (entry: ChallengeProgressEntry) => {
    originalEntry.value = {...entry}; // Create a copy to avoid reference issues

    // Set form values
    challenged.value = entry.challenged ?? 1; // Use nullish coalescing
    weight.value = entry.weight ?? 0;
    challengeDuration.value = entry.challengeDuration ?? 0;
    reason.value = '';

    // Reset errors
    errors.value.reason = '';
};

const validate = () => {
    // Reset errors
    errors.value.reason = '';

    // Validate reason if there are changes
    if (hasChanges.value && !reason.value.trim()) {
        errors.value.reason = 'Reason for revision is required';
        return false;
    }

    return true;
};

const decrementChallengedRevision = () => {
    if (isChallengedDisabled.value) return;
    if (challenged.value > 0) {
        challenged.value--;
    }
};

const handleSubmit = async () => {
    if (!validate() || !props.entry || !hasChanges.value || !isValid.value) {
        return;
    }

    const oldChallenged = originalEntry.value?.challenged;
    const oldWeight = originalEntry.value?.weight;
    const oldDuration = originalEntry.value?.challengeDuration;

    if (oldChallenged === challenged.value && oldWeight === weight.value && oldDuration === challengeDuration.value) {
        return;
    }

    // Create a single record with all changes
    const success = await recordChange({
        fieldPath: `configurations[${configIndex.value}].progressData[${props.entryIndex}]`,
        fieldName: 'Challenge Administration',
        oldValue: {
            challenged: oldChallenged,
            weight: oldWeight,
            challengeDuration: oldDuration
        },
        newValue: {
            challenged: challenged.value,
            weight: weight.value,
            challengeDuration: challengeDuration.value
        },
        context: 'ChallengeProgress',
        contextType: 'edit',
        workflowStage: 'execution',
        requireJustification: false,
        justification: reason.value
    });

    if (success) {
        addRevisionToRecord(
            props.configKey,
            props.entryIndex,
            {
                challenged: challenged.value,
                weight: weight.value,
                challengeDuration: challengeDuration.value
            },
            {
                challenged: oldChallenged,
                weight: oldWeight,
                challengeDuration: oldDuration
            },
            reason.value
        );

        updateChallengeRecord(props.configKey, props.entryIndex, {
            challenged: challenged.value,
            weight: weight.value,
            challengeDuration: challengeDuration.value
        });

        handleClose();
    }
};

const handleClose = () => {
    resetForm();
    originalEntry.value = null;
    errors.value.reason = ''
    if (props.onClose) {
        props.onClose();
    }
};

// ===================================================================
// Watchers
// ===================================================================
watch(() => props.isOpen, (isOpen) => {
    if (isOpen && props.entry) {
        setEntry(props.entry);
    }
});

watch(() => props.configKey, (key) => {
    if (!key) {
        console.error('No configuration key provided');
        return;
    }

    const configs = configurations.value;

    // Defensive check for configurations
    if (!configs || configs.length === 0) {
        console.error('No configurations available');
        return;
    }

    // Find the exact matching configuration
    const foundConfig = configs.find(config => config.key === key);

    if (!foundConfig) {
        console.error(`No configuration found for key: ${key}`);

        configIndex.value = -1;
        if (props.onClose) props.onClose();

        return;
    }

    // Find the index of the matched configuration
    configIndex.value = configs.indexOf(foundConfig);
}, {immediate: true});
</script>

<template>
    <ModalBox v-if="isOpen" @close="handleClose">
        <template #header>
            <h2 class="text-xl font-bold text-gray-800">Update Challenge Administration Entry</h2>
        </template>
        <template #main>
            <!-- Body -->
            <div class="flex-1 overflow-y-auto">
                <form class="space-y-6" @submit.prevent="handleSubmit">
                    <div class="grid grid-cols-2 gap-4">
                        <!-- Challenged Fish field -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                                No. of Challenged Fish
                            </label>
                            <div class="flex">
                                <button
                                    :class="{ 'opacity-50 cursor-not-allowed': isChallengedDisabled || challenged <= 0 }"
                                    :disabled="isChallengedDisabled || challenged <= 0"
                                    class="p-2 border border-primary rounded-l-md bg-primary text-white"
                                    type="button"
                                    @click="decrementChallengedRevision"
                                >
                                    <svg
                                        class="h-4 w-4"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            clip-rule="evenodd"
                                            d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                            fill-rule="evenodd"
                                        />
                                    </svg>
                                </button>
                                <input
                                    v-model.number="challenged"
                                    :class="{ 'opacity-50 cursor-not-allowed': isChallengedDisabled }"
                                    :disabled="isChallengedDisabled"
                                    class="w-full px-3 py-2 border-t border-b border-gray-300 focus:outline-none focus:ring-primary focus:border-primary text-center"
                                    type="number"
                                />
                                <button
                                    :class="{ 'opacity-50 cursor-not-allowed': isChallengedDisabled }"
                                    :disabled="isChallengedDisabled"
                                    class="p-2 border border-primary rounded-r-md bg-primary text-white"
                                    type="button"
                                    @click="incrementChallenged"
                                >
                                    <svg
                                        class="h-4 w-4"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            clip-rule="evenodd"
                                            d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                            fill-rule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <!-- Weight field -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                                Weight (Grams)
                            </label>
                            <input
                                v-model.number="weight"
                                :class="{ 'opacity-50 cursor-not-allowed': isWeightDisabled }"
                                :disabled="isWeightDisabled"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                type="number"
                            />
                        </div>
                    </div>


                    <!-- Reason field -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            Reason for revision
                        </label>
                        <textarea
                            v-model="reason"
                            :class="{ 'border-red-500': errors.reason }"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                            rows="3"
                        ></textarea>
                        <p v-if="errors.reason" class="mt-1 text-sm text-red-600">
                            {{ errors.reason }}
                        </p>
                    </div>

                    <!-- Revisions history -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 mb-3">Revisions:</h3>
                        <div class="border border-gray-200 rounded-lg">
                            <!-- Fixed header table -->
                            <div class="w-full">
                                <table class="min-w-full divide-y divide-gray-200">
                                    <thead class="bg-gray-50">
                                    <tr class="border-b">
                                        <th
                                            class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5"
                                            scope="col"
                                        >
                                            Challenged Fish
                                        </th>
                                        <th
                                            class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5"
                                            scope="col"
                                        >
                                            Weight
                                        </th>
                                        <th
                                            class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5"
                                            scope="col"
                                        >
                                            Justification
                                        </th>
                                    </tr>
                                    </thead>
                                </table>
                            </div>

                            <div class="max-h-[150px] overflow-y-auto">
                                <table class="min-w-full divide-y divide-gray-200">
                                    <tbody class="bg-white divide-y divide-gray-200">
                                    <tr v-if="formattedHistory.length === 0">
                                        <td class="px-4 py-4 text-sm text-center text-gray-500" colspan="4">
                                            No revision history available
                                        </td>
                                    </tr>
                                    <tr v-for="(item, index) in formattedHistory" :key="index" class="hover:bg-gray-50">
                                        <!-- Challenged Fish column -->
                                        <td class="px-4 py-4 text-sm w-1/5">
                                            <span class="flex items-center">
                                              <span
                                                  v-if="item.oldChallenged !== undefined"
                                                  class="bg-red-100 text-red-800 px-2 py-1 rounded"
                                              >{{ item.oldChallenged }}</span>
                                              <span v-if="item.oldChallenged !== undefined && item.newChallenged !== undefined" class="mx-2">→</span>
                                              <span v-if="item.newChallenged !== undefined" class="bg-green-100 text-green-800 px-2 py-1 rounded">{{
                                                      item.newChallenged
                                                  }}</span>
                                            </span>
                                        </td>

                                        <!-- Weight column -->
                                        <td class="px-4 py-4 text-sm w-1/5">
                                            <span class="flex items-center">
                                              <span
                                                  v-if="item.oldWeight !== undefined"
                                                  class="bg-red-100 text-red-800 px-2 py-1 rounded"
                                              >{{ item.oldWeight || 'N/A' }}</span>
                                              <span
                                                  v-if="item.oldWeight !== undefined && item.newWeight !== undefined"
                                                  class="mx-2"
                                              >→</span>
                                              <span
                                                  v-if="item.newWeight !== undefined"
                                                  class="bg-green-100 text-green-800 px-2 py-1 rounded"
                                              >{{ item.newWeight || 'N/A' }}</span>
                                            </span>
                                        </td>


                                        <!-- Justification column -->
                                        <td class="px-4 py-4 text-sm text-gray-500 w-2/5">
                                            {{ item.reason || 'No justification provided' }}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </template>
        <template #footer>
            <!-- Footer -->
            <div class="flex justify-end space-x-3">
                <button
                    class="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    type="button"
                    @click="handleClose"
                >
                    Cancel
                </button>
                <button
                    :class="{ 'opacity-50 cursor-not-allowed': !hasChanges || !isValid }"
                    :disabled="!hasChanges || !isValid"
                    class="px-4 py-2 bg-primary border border-transparent rounded-md text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    type="button"
                    @click="handleSubmit"
                >
                    Save Changes
                </button>
            </div>
        </template>
    </ModalBox>
</template>
